import React, { useState, useEffect, useCallback, useRef } from 'react';
import useWindowRows, { chunkRows } from './useWindowRows';
import {
    Box,
    Typography,
    Paper,
    Button,
    CircularProgress,
    Dialog,
    Card,
    CardContent,
    Chip,
    IconButton,
    Fade,
    Grid,
    Divider,
    alpha,
    useTheme,
    useMediaQuery,
    TextField,
    MenuItem,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Checkbox,
    Avatar,
    Modal,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Toolbar,
    Tooltip,
    ToggleButton,
    ToggleButtonGroup,
    Autocomplete
} from '@mui/material';
import {
    Add as AddIcon,
    Payment as PaymentIcon,
    CalendarToday as CalendarIcon,
    Money as MoneyIcon,
    Print as PrintIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
    TrendingUp as TrendingUpIcon,
    Receipt as ReceiptIcon,
    FilterList as FilterListIcon,
    Search as SearchIcon,
    Close as CloseIcon,
    ScheduleSend as ScheduleSendIcon,
    ViewList as ViewListIcon,
    ViewModule as ViewModuleIcon,
    WhatsApp as WhatsAppIcon,
    LocationOn as LocationIcon,
    CardGiftcard as CardGiftcardIcon,
    Phone as PhoneIcon,
    Undo as UndoIcon,
    SwapHoriz as MethodIcon,
    MoneyOff as RefundIcon,
    ContentCopy as ContentCopyIcon,
    Link as LinkIcon,
    Email as EmailIcon
} from '@mui/icons-material';
import { useAppContext } from '../context/AppContext.js';
import CreditReviewDialog from './CreditReviewDialog';
import { escapeHtml } from '../utils/escapeHtml.js';


// Totals for the current filters, computed by the server over every matching
// payment (GET /api/payments -> totals), not just the rows loaded here.
const TOTAL_TILES = [
    { key: 'collected', label: 'Collected', color: '#0EA5E9' },
    { key: 'uncollected', label: 'Uncollected', color: '#F59E0B' },
    { key: 'unpaid', label: 'Unpaid', color: '#EF4444' },
    { key: 'paid', label: 'Paid', color: '#10B981' },
];

function PaymentTotalsBar({ totals, loading }) {
    const fmt = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 2, mb: 3 }}>
            {TOTAL_TILES.map(({ key, label, color }) => {
                const t = totals?.[key];
                return (
                    <Paper key={key} elevation={0} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${alpha(color, 0.25)}`, bgcolor: alpha(color, 0.04) }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.3 }}>
                            {loading && !t ? '…' : fmt(t?.amount)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {t ? `${t.count} payment${t.count === 1 ? '' : 's'}` : '—'}
                            {key === 'collected' && t?.awaiting_count ? ` · ${fmt(t.awaiting_amount)} awaiting confirmation` : ''}
                        </Typography>
                    </Paper>
                );
            })}
        </Box>
    );
}

// Revenue helpers — kept outside component so they are never recreated.
// A refund row gives money back, so it subtracts; a gratis (forgiven) bill is
// marked paid but brought no money in, so it adds nothing.
const revenueAmount = (p) => (p.is_gratis ? 0 : (p.is_refund ? -1 : 1) * (parseFloat(p.amount) || 0));

const getTotalRevenue = (payments) => {
    if (!Array.isArray(payments)) return 0;
    return payments.reduce((sum, p) => p.paid ? sum + revenueAmount(p) : sum, 0);
};

const getCurrentMonthRevenue = (payments) => {
    if (!Array.isArray(payments)) return 0;
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    return payments.reduce((sum, p) => {
        if (!p.paid) return sum;
        const d = new Date(p.date);
        return (d.getFullYear() === y && d.getMonth() === m) ? sum + revenueAmount(p) : sum;
    }, 0);
};



// Modal style for consistent popups
const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
    borderRadius: '8px',
};

// --- NEW: Debounced search component to prevent typing lag ---
const DebouncedSearchInput = ({ value, onChange, ...props }) => {
    const [localValue, setLocalValue] = useState(value || '');
    const onChangeRef = useRef(onChange);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (localValue !== value) {
                onChangeRef.current(localValue);
            }
        }, 500);
        return () => clearTimeout(timeoutId);
    }, [localValue, value]);

    return (
        <TextField
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            {...props}
        />
    );
};

// --- NEW: Component for the specific printable receipt format ---
const PrintableReceipt = React.forwardRef(({ receiptData }, ref) => {
    if (!receiptData) return null;

    const arabicMonths = [
        'كانون الثاني', 'شباط', 'آذار', 'نيسان', 'أيار', 'حزيران',
        'تموز', 'آب', 'أيلول', 'تشرين الأول', 'تشرين الثاني', 'كانون الأول'
    ];
    const date = new Date(receiptData.payment_date);
    const month = date.getMonth();
    const year = date.getFullYear();
    const arabicMonthYear = `${arabicMonths[month]} ${year}`;

    const currentBalance = (parseFloat(receiptData.customer_new_balance) || 0).toFixed(2);
    const previousBalance = ((parseFloat(receiptData.customer_new_balance) || 0) + (parseFloat(receiptData.amount_paid_now) || 0)).toFixed(2);



    return (
        <Box
            ref={ref}
            id="receipt-to-print"
            sx={{
                width: '21cm',
                height: '7.5cm',
                display: 'flex',
                fontFamily: 'Arial, sans-serif',
                margin: '0 auto',
                backgroundColor: 'white',
                paddingTop: '40px',
                boxSizing: 'border-box',
                border: 'none',
                direction: 'rtl',
                color: '#000' // Ensure text is black
            }}
        >
            {/* Main Part - 14.1cm (Right Side) */}
            <Box
                sx={{
                    width: '14.1cm',
                    padding: '10px 15px 10px 15px',
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'row-reverse',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                        fontSize: '18px',
                        flexGrow: 1,
                        paddingRight: '15px',
                        width: '100%'
                    }}
                >
                    <Typography component="span" sx={{ display: 'block' }}>
                        الإسم: {receiptData.customer_name}
                    </Typography>
                    <Typography component="span" sx={{ display: 'block' }}>
                        العنوان: {receiptData.customer_address}
                    </Typography>
                    <Typography component="span" sx={{ display: 'block' }}>
                        الهاتف: {receiptData.customer_phone}
                    </Typography>
                    <Typography component="span" sx={{ display: 'block' }}>
                        تاريخ الإيصال: {receiptData.payment_date}
                    </Typography>
                    <Typography component="span" sx={{ display: 'block' }}>
                        المبلغ الشهري المستحق: ${(parseFloat(receiptData.subscription_plan_details?.price) || 0).toFixed(2)} / الخدمة: {receiptData.subscription_plan_details?.name || 'انترنت'}
                    </Typography>
                    <Typography component="span" sx={{ display: 'block' }}>
                        عن شهر: {arabicMonthYear}
                    </Typography>
                    <Typography component="span" sx={{ display: 'block', fontWeight: 'bold' }}>
                        الرصيد الحالي: ${currentBalance} / الرصيد السابق: ${previousBalance}
                    </Typography>
                </Box>
            </Box>

            {/* Mini Part - 6.9cm (Left Side) */}
            <Box
                sx={{
                    width: '6.9cm',
                    padding: '10px 15px 10px 15px',
                    boxSizing: 'border-box',
                    borderRight: '1px dashed #000', // Add a separator line
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    fontSize: '14px',
                }}
            >
                <Box
                    sx={{
                        flexGrow: 1,
                        fontSize: '16px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                        width: '100%'
                    }}
                >
                    <Typography component="span" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        الإسم: {receiptData.customer_name}
                    </Typography>
                    <Typography component="span" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        العنوان: {receiptData.customer_address}
                    </Typography>
                    <Typography component="span" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        الهاتف: {receiptData.customer_phone}
                    </Typography>
                    <Typography component="span" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        المبلغ الشهري: ${(parseFloat(receiptData.subscription_plan_details?.price) || 0).toFixed(2)}
                    </Typography>
                    <Typography component="span" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        الرصيد الحالي: ${currentBalance}
                    </Typography>
                    <Typography component="span" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        الرصيد السابق: ${previousBalance}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
});


// ─────────────────────────────────────────────────────────────────────────────
// Whether staff can switch how this payment was received (cash <-> Whish
// transfer). Mirrors set_payment_method in app.py: collected or paid, not
// gratis/refund, and not settled by the Whish gateway itself.
const canChangeMethod = (payment) =>
    (payment.collected || payment.paid) && !payment.is_gratis && !payment.is_refund && payment.collected_via !== 'whish';

// Money that actually came in can be (partly) given back -- see refund_payment in app.py.
const canRefund = (payment) => payment.paid && !payment.is_gratis && !payment.is_refund;

// How a refund is paid out by default: the same way the money came in.
const defaultRefundVia = (payment) =>
    (payment.collected_via === 'whish' || payment.collected_via === 'whish_transfer') ? 'whish' : 'cash';

// A refund row's channel lives on collected_via (see refund_payment in app.py).
const refundChipLabel = (payment) =>
    `Refund · ${payment.collected_via === 'whish_transfer' ? 'Whish' : 'Cash'}`;

const EMPTY_REFUND = { open: false, payment: null, amount: '', paidVia: 'cash', reason: '' };

// _PaymentCard — module-level so React.memo actually memoizes between renders.
// All event-handler props are passed in via cardHandlers (stable useMemo object).
// ─────────────────────────────────────────────────────────────────────────────
const PaymentCardItem = React.memo(({
    payment,
    getStatusColor, getPaymentTypeColor,
    openMarkPaidDialog, openMarkGratisDialog, openRevertDialog, openMethodDialog, openRefundDialog, handlePrepareReceipt, handleDeletePayment,
    buildWhatsAppLink, waSettings, userRoles,
    twsEnabled, handleSendPaymentLink, sendLinkLoading,
}) => {
    const isCollector = userRoles.includes('collector') || userRoles.includes('admin') || userRoles.includes('finance');
    const isAdminOrFinance = userRoles.includes('admin') || userRoles.includes('finance');

    const theme = useTheme();
    return (
        <Fade in timeout={300}>
            <Card
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 24px ${alpha(theme.palette.common.black, 0.15)}`,
                        '& .payment-actions': { opacity: 1, transform: 'translateX(0)' }
                    },
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    mb: 2
                }}
            >
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: `linear-gradient(90deg, ${getStatusColor(payment.paid)}, ${alpha(getStatusColor(payment.paid), 0.7)})` }} />
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
                            <Avatar sx={{ width: 56, height: 56, background: `linear-gradient(135deg, ${getPaymentTypeColor(payment.pre_payment)}, ${alpha(getPaymentTypeColor(payment.pre_payment), 0.7)})`, fontSize: '1.25rem', fontWeight: 700 }}>
                                {payment.customer_name ? payment.customer_name.charAt(0).toUpperCase() : 'N'}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.2 }}>{payment.customer_name || 'Unknown Customer'}</Typography>
                                {payment.customer_address && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                        <LocationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                        <Typography variant="caption" color="text.secondary">{payment.customer_address}</Typography>
                                    </Box>
                                )}
                                {payment.customer_phone && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                        <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                        <Typography variant="caption" color="text.secondary">{payment.customer_phone}</Typography>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">Billed: {new Date(payment.date).toLocaleDateString()}</Typography>
                                </Box>
                                {payment.paid_at && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <CheckCircleIcon sx={{ fontSize: 14, color: theme.palette.success.main }} />
                                        <Typography variant="body2" color="text.secondary">Paid: {new Date(payment.paid_at).toLocaleDateString()}</Typography>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <MoneyIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: getStatusColor(payment.paid) }}>
                                        ${(parseFloat(payment.amount) || 0).toFixed(2)}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                            <Chip label={payment.paid ? 'Paid' : (payment.collected ? (payment.collected_amount ? `Collected ($${payment.collected_amount.toFixed(2)})` : 'Collected') : 'Unpaid')} size="small"
                                sx={{ backgroundColor: alpha(getStatusColor(payment.paid), 0.1), color: getStatusColor(payment.paid), fontWeight: 600, fontSize: '0.75rem', border: `1px solid ${alpha(getStatusColor(payment.paid), 0.2)}` }} />
                            {payment.is_refund && (
                                <Chip label={refundChipLabel(payment)} size="small" title={payment.refund_reason || ''}
                                    sx={{ backgroundColor: alpha('#8B5CF6', 0.08), color: '#8B5CF6', fontWeight: 700, fontSize: '0.75rem', border: `1px solid ${alpha('#8B5CF6', 0.25)}` }} />
                            )}
                            {payment.collected_via === 'whish_transfer' && !payment.is_refund && (
                                <Chip label="Whish transfer" size="small" title={payment.whish_transaction_number ? `Ref: ${payment.whish_transaction_number}` : ''}
                                    sx={{ mr: 0.5, backgroundColor: alpha('#E11D48', 0.08), color: '#E11D48', fontWeight: 700, fontSize: '0.75rem', border: `1px solid ${alpha('#E11D48', 0.25)}` }} />
                            )}
                            {payment.is_gratis && (
                                <Chip label="GRATIS" size="small" title={payment.gratis_note || ''}
                                    sx={{ backgroundColor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main, fontWeight: 700, fontSize: '0.75rem', border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }} />
                            )}
                            {payment.pre_payment && (
                                <Chip label="Pre-Payment" size="small"
                                    sx={{ backgroundColor: alpha(theme.palette.secondary.main, 0.1), color: theme.palette.secondary.main, fontWeight: 600, fontSize: '0.75rem', border: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}` }} />
                            )}
                            {payment.reason && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                    Reason: {payment.reason}
                                </Typography>
                            )}
                            {payment.collected && !payment.paid && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    by {payment.collected_by} {payment.collected_amount ? `($${payment.collected_amount.toFixed(2)})` : ''}
                                </Typography>
                            )}
                            {payment.paid && (payment.received_by || payment.collected_via === 'whish') && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    {payment.received_by
                                        ? `rcvd by ${payment.received_by}`
                                        : `via Whish${payment.whish_transaction_number ? ` (#${payment.whish_transaction_number})` : ''}`}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Divider sx={{ my: 2, opacity: 0.6 }} />
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                        {!payment.paid && (
                            <>
                                {((!payment.collected && isCollector) || (payment.collected && isAdminOrFinance)) && (
                                    <Button size="small" variant="outlined" startIcon={<CheckCircleIcon />} onClick={() => openMarkPaidDialog(payment)} sx={{ borderColor: alpha('#10B981', 0.3), color: '#10B981', '&:hover': { borderColor: '#10B981', backgroundColor: alpha('#10B981', 0.05) } }}>
                                        {payment.collected ? 'Confirm Receipt' : 'Collect'}
                                    </Button>
                                )}
                                {isAdminOrFinance && (
                                    <Button size="small" variant="outlined" startIcon={<CardGiftcardIcon />} onClick={() => openMarkGratisDialog(payment)} sx={{ borderColor: alpha(theme.palette.info.main, 0.3), color: theme.palette.info.main, '&:hover': { borderColor: theme.palette.info.main, backgroundColor: alpha(theme.palette.info.main, 0.05) } }}>
                                        Mark Gratis
                                    </Button>
                                )}
                                {isAdminOrFinance && (
                                    <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={() => handlePrepareReceipt(payment.id)} sx={{ borderColor: alpha(theme.palette.primary.main, 0.3), '&:hover': { borderColor: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.05) } }}>
                                        Print Receipt
                                    </Button>
                                )}
                                {isAdminOrFinance && twsEnabled && (
                                    <Button size="small" variant="outlined" startIcon={<LinkIcon />} disabled={sendLinkLoading}
                                        onClick={() => handleSendPaymentLink(payment)}
                                        sx={{ borderColor: alpha(theme.palette.primary.main, 0.3), '&:hover': { borderColor: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.05) } }}>
                                        Send Payment Link
                                    </Button>
                                )}
                            </>
                        )}
                        {payment.paid && isAdminOrFinance && (
                            <Button size="small" variant="outlined" startIcon={<PrintIcon />} onClick={() => handlePrepareReceipt(payment.id)} sx={{ borderColor: alpha(theme.palette.primary.main, 0.3), '&:hover': { borderColor: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.05) } }}>
                                Print Receipt
                            </Button>
                        )}
                        {isAdminOrFinance && waSettings.enabled && waSettings.mode === 'deeplink' && (payment.paid || payment.collected) && (() => {
                            const link = buildWhatsAppLink(payment);
                            return link ? (
                                <Button size="small" variant="outlined" startIcon={<WhatsAppIcon />}
                                    component="a" href={link} target="_blank" rel="noopener noreferrer"
                                    sx={{ borderColor: alpha('#25D366', 0.4), color: '#25D366', '&:hover': { borderColor: '#25D366', backgroundColor: alpha('#25D366', 0.06) } }}>
                                    WhatsApp
                                </Button>
                            ) : null;
                        })()}
                        {isAdminOrFinance && canChangeMethod(payment) && (
                            <Button size="small" variant="outlined" startIcon={<MethodIcon />} onClick={() => openMethodDialog(payment)} sx={{ borderColor: alpha('#E11D48', 0.3), color: '#E11D48', '&:hover': { borderColor: '#E11D48', backgroundColor: alpha('#E11D48', 0.05) } }}>
                                Method
                            </Button>
                        )}
                        {isAdminOrFinance && canRefund(payment) && (
                            <Button size="small" variant="outlined" startIcon={<RefundIcon />} onClick={() => openRefundDialog(payment)} sx={{ borderColor: alpha('#8B5CF6', 0.3), color: '#8B5CF6', '&:hover': { borderColor: '#8B5CF6', backgroundColor: alpha('#8B5CF6', 0.05) } }}>
                                Refund
                            </Button>
                        )}
                        {payment.paid && isAdminOrFinance && (
                            <Button size="small" variant="outlined" startIcon={<UndoIcon />} onClick={() => openRevertDialog(payment)} sx={{ borderColor: alpha('#F59E0B', 0.3), color: '#F59E0B', '&:hover': { borderColor: '#F59E0B', backgroundColor: alpha('#F59E0B', 0.05) } }}>
                                Revert
                            </Button>
                        )}
                        {isAdminOrFinance && (
                            <Button size="small" variant="outlined" startIcon={<DeleteIcon />} onClick={() => handleDeletePayment && handleDeletePayment(payment.id)} sx={{ borderColor: alpha('#EF4444', 0.3), color: '#EF4444', '&:hover': { borderColor: '#EF4444', backgroundColor: alpha('#EF4444', 0.05) } }}>
                                Delete
                            </Button>
                        )}
                    </Box>
                </CardContent>
            </Card>
        </Fade>
    );
});

// One list-view row, memoized: the row markup used to live inline in
// PaymentsView, so every checkbox tick re-rendered every row (~2s with 300
// payments). `actions` is a stable object forwarding to the view's latest
// handlers; the role flags are booleans so they compare by value.
const PaymentListRow = React.memo(function PaymentListRow({
    payment, index, sel, canCollect, isAdminOrFinance, waSettings, actions, measureRef,
}) {
    const theme = useTheme();
    // Same colors as PaymentsView's getStatusColor/getPaymentTypeColor.
    const paymentStatusColor = (isPaid) => (isPaid ? theme.palette.success.main : theme.palette.error.main);
    const paymentTypeColor = (isPrePayment) => (isPrePayment ? theme.palette.secondary.main : theme.palette.primary.main);
    return (
        <TableRow
            ref={measureRef}
            data-index={index}
            hover
            selected={sel}
            sx={{ '&.Mui-selected': { bgcolor: alpha(theme.palette.primary.main, 0.06) } }}
        >
            <TableCell padding="checkbox">
                <Checkbox checked={sel} onChange={() => actions.handleSelectOne(payment.id)} />
            </TableCell>
            <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 32, height: 32, fontSize: '0.85rem', fontWeight: 700, background: `linear-gradient(135deg, ${paymentTypeColor(payment.pre_payment)}, ${alpha(paymentTypeColor(payment.pre_payment), 0.7)})` }}>
                        {(payment.customer_name || 'N').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{payment.customer_name || '—'}</Typography>
                        {payment.customer_address && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{payment.customer_address}</Typography>
                        )}
                        {payment.customer_phone && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{payment.customer_phone}</Typography>
                        )}
                    </Box>
                </Box>
            </TableCell>
            <TableCell><Typography variant="body2">{new Date(payment.date).toLocaleDateString()}</Typography></TableCell>
            <TableCell><Typography variant="body2">{payment.paid_at ? new Date(payment.paid_at).toLocaleDateString() : '—'}</Typography></TableCell>
            <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700, color: paymentStatusColor(payment.paid) }}>
                    ${(parseFloat(payment.amount) || 0).toFixed(2)}
                </Typography>
            </TableCell>
            <TableCell>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip label={payment.paid ? 'Paid' : 'Unpaid'} size="small"
                        sx={{ bgcolor: alpha(paymentStatusColor(payment.paid), 0.1), color: paymentStatusColor(payment.paid), fontWeight: 600, border: `1px solid ${alpha(paymentStatusColor(payment.paid), 0.25)}` }}
                    />
                    {payment.is_gratis && (
                        <Chip label="GRATIS" size="small" title={payment.gratis_note || ''}
                            sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: theme.palette.info.main, fontWeight: 700, border: `1px solid ${alpha(theme.palette.info.main, 0.25)}` }}
                        />
                    )}
                    {payment.is_refund && (
                        <Chip label={refundChipLabel(payment)} size="small" title={payment.refund_reason || ''}
                            sx={{ bgcolor: alpha('#8B5CF6', 0.08), color: '#8B5CF6', fontWeight: 700, border: `1px solid ${alpha('#8B5CF6', 0.25)}` }}
                        />
                    )}
                    {payment.collected_via === 'whish_transfer' && !payment.is_refund && (
                        <Chip label="Whish transfer" size="small" title={payment.whish_transaction_number ? `Ref: ${payment.whish_transaction_number}` : ''}
                            sx={{ bgcolor: alpha('#E11D48', 0.08), color: '#E11D48', fontWeight: 700, border: `1px solid ${alpha('#E11D48', 0.25)}` }}
                        />
                    )}
                </Box>
            </TableCell>
            <TableCell>
                {payment.pre_payment ? (
                    <Chip label="Pre-Pay" size="small" sx={{ bgcolor: alpha(theme.palette.secondary.main, 0.1), color: theme.palette.secondary.main, fontWeight: 600 }} />
                ) : '—'}
            </TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {!payment.paid && (
                    (!payment.collected && canCollect) ||
                    (payment.collected && isAdminOrFinance)
                ) && (
                    <Tooltip title={payment.collected ? "Confirm Receipt" : "Collect"}>
                        <IconButton size="small" color="success" onClick={() => actions.openMarkPaidDialog(payment)}>
                            <CheckCircleIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {!payment.paid && isAdminOrFinance && (
                    <Tooltip title="Mark Gratis (Free)">
                        <IconButton size="small" color="info" onClick={() => actions.openMarkGratisDialog(payment)}>
                            <CardGiftcardIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {isAdminOrFinance && (
                    <Tooltip title="Print Receipt">
                        <IconButton size="small" color="primary" onClick={() => actions.handlePrepareReceipt(payment.id)}>
                            <PrintIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {isAdminOrFinance && waSettings.enabled && waSettings.mode === 'deeplink' && (payment.paid || payment.collected) && (() => {
                    const waLink = actions.buildWhatsAppLink(payment);
                    return waLink ? (
                        <Tooltip title="Send via WhatsApp">
                            <IconButton size="small" component="a" href={waLink} target="_blank" rel="noopener noreferrer" sx={{ color: '#25D366' }}>
                                <WhatsAppIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    ) : null;
                })()}
                {isAdminOrFinance && canChangeMethod(payment) && (
                    <Tooltip title="Change payment method (cash / Whish transfer)">
                        <IconButton size="small" sx={{ color: '#E11D48' }} onClick={() => actions.openMethodDialog(payment)}>
                            <MethodIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {isAdminOrFinance && canRefund(payment) && (
                    <Tooltip title="Refund">
                        <IconButton size="small" sx={{ color: '#8B5CF6' }} onClick={() => actions.openRefundDialog(payment)}>
                            <RefundIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {payment.paid && isAdminOrFinance && (
                    <Tooltip title="Revert to Pending">
                        <IconButton size="small" sx={{ color: '#F59E0B' }} onClick={() => actions.openRevertDialog(payment)}>
                            <UndoIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {isAdminOrFinance && (
                    <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => actions.handleDeletePayment(payment.id)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </TableCell>
        </TableRow>
    );
});

const PaymentsView = () => {
    const { user, apiService, setSnackbar, hasModule, modules } = useAppContext();
    const parsedRoles = user?.role ? user.role.split(',').map(r => r.trim().toLowerCase()) : [];
    // On this page an office 'cashier' has exactly a collector's rights
    // (collect, never confirm receipt -- see mark_payment_as_paid), so fold
    // it in rather than repeating 'cashier' next to every collector check.
    const userRoles = parsedRoles.includes('cashier') && !parsedRoles.includes('collector')
        ? [...parsedRoles, 'collector'] : parsedRoles;

    const theme = useTheme();
    const [payments, setPayments] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentTab, setCurrentTab] = useState(0);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [selected, setSelected] = useState([]); // selected payment IDs for bulk actions
    const [bulkActionLoading, setBulkActionLoading] = useState(false);
    const [markPaidSubmitting, setMarkPaidSubmitting] = useState(false);
    // Mark-as-Paid dialog
    const [markPaidDialog, setMarkPaidDialog] = useState({ open: false, paymentId: null, outstanding: 0, customerName: '' });
    const [markPaidAmount, setMarkPaidAmount] = useState('');
    const [paymentTotals, setPaymentTotals] = useState(null);
    // Chosen when collecting (not when confirming receipt): cash or a direct Whish transfer.
    const [markPaidMethod, setMarkPaidMethod] = useState('cash');
    const [markPaidReference, setMarkPaidReference] = useState('');
    // Confirming one collected card can confirm the customer's other collected cards too.
    const [confirmAllCollected, setConfirmAllCollected] = useState(true);
    const [creditReviewOpen, setCreditReviewOpen] = useState(false);
    const [creditReviewCount, setCreditReviewCount] = useState(0);
    // Mark-as-Gratis dialog
    const [markGratisDialog, setMarkGratisDialog] = useState({ open: false, paymentId: null, outstanding: 0, customerName: '' });
    const [markGratisNote, setMarkGratisNote] = useState('');
    const [markGratisSubmitting, setMarkGratisSubmitting] = useState(false);
    // Revert-payment dialog
    const [revertDialog, setRevertDialog] = useState({ open: false, paymentId: null, outstanding: 0, customerName: '' });
    // Cash vs Whish transfer for a collected/paid payment (see canChangeMethod).
    const [methodDialog, setMethodDialog] = useState({ open: false, payment: null, method: 'cash', reference: '' });
    const [methodSubmitting, setMethodSubmitting] = useState(false);
    const [revertReason, setRevertReason] = useState('');
    // Refund dialog: amount (up to the original), payout channel and reason.
    const [refundDialog, setRefundDialog] = useState(EMPTY_REFUND);
    const [refundSubmitting, setRefundSubmitting] = useState(false);
    const [revertSubmitting, setRevertSubmitting] = useState(false);
    const [waSettings, setWaSettings] = useState({ enabled: false, mode: 'deeplink', deeplink_msg_payment: 'Dear {customer_name}, your payment of ${amount} has been received. Thank you!', deeplink_msg_payment_link: 'Hi {customer_name}, here is your payment link: {pay_url}' });
    const [twsEnabled, setTwsEnabled] = useState(false);

    // Fetch WhatsApp settings once
    useEffect(() => {
        apiService.fetchWhatsAppDeeplinkSettings().then(res => {
            if (res.data?.settings) setWaSettings(res.data.settings);
        }).catch(() => {});
    }, [apiService]);

    // Fetch tenant-Whish (customer payments) settings once -- gates the
    // "Send payment link" button below.
    useEffect(() => {
        if (!hasModule('whish_payments')) { setTwsEnabled(false); return; }
        apiService.tenantWhishSettings().then(res => {
            setTwsEnabled(!!res.data?.settings?.enabled);
        }).catch(() => {});
    }, [apiService, modules]); // eslint-disable-line react-hooks/exhaustive-deps

    // Send-payment-link dialog: shown after a successful resend, offering
    // WhatsApp / copy-link / email delivery for the fresh link.
    const [sendLinkDialog, setSendLinkDialog] = useState({ open: false, payment: null, payUrl: '', viewToken: '' });
    const [sendLinkLoading, setSendLinkLoading] = useState(false);
    const [emailAddress, setEmailAddress] = useState('');
    const [emailSending, setEmailSending] = useState(false);

    const handleSendPaymentLink = async (payment) => {
        setSendLinkLoading(true);
        try {
            const res = await apiService.resendWhishPaymentLink(payment.customer_id, payment.id);
            setSendLinkDialog({ open: true, payment, payUrl: res.data.pay_url, viewToken: res.data.view_token });
            setEmailAddress('');
        } catch (err) {
            const detail = err?.response?.data?.msg || 'Could not create a payment link.';
            setSnackbar({ open: true, message: detail, severity: 'error' });
        } finally {
            setSendLinkLoading(false);
        }
    };

    const closeSendLinkDialog = () => setSendLinkDialog({ open: false, payment: null, payUrl: '', viewToken: '' });

    const sendLinkWhatsAppUrl = () => {
        const { payment, payUrl } = sendLinkDialog;
        const phone = (payment?.customer_phone || '').replace(/\D/g, '');
        if (!phone) return null;
        const msg = (waSettings.deeplink_msg_payment_link || 'Hi {customer_name}, here is your payment link: {pay_url}')
            .replace('{customer_name}', payment?.customer_name || '')
            .replace('{pay_url}', payUrl);
        return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    };

    const copyPaymentLink = async () => {
        try {
            await navigator.clipboard.writeText(sendLinkDialog.payUrl);
            setSnackbar({ open: true, message: 'Link copied!', severity: 'success' });
        } catch {
            setSnackbar({ open: true, message: 'Could not copy link.', severity: 'error' });
        }
    };

    const emailPaymentLink = async () => {
        if (!emailAddress.trim()) return;
        setEmailSending(true);
        try {
            const { payment, payUrl } = sendLinkDialog;
            await apiService.emailWhishPaymentLink(payment.customer_id, payment.id, emailAddress.trim(), payUrl);
            setSnackbar({ open: true, message: 'Email sent!', severity: 'success' });
        } catch (err) {
            const detail = err?.response?.data?.msg || 'Could not send the email.';
            setSnackbar({ open: true, message: detail, severity: 'error' });
        } finally {
            setEmailSending(false);
        }
    };

    // Build wa.me deep-link for a paid or collected payment
    const buildWhatsAppLink = (payment) => {
        const phone = (payment.customer_phone || '').replace(/\D/g, '');
        if (!phone) return null;
        const amount = payment.paid ? payment.amount : (payment.collected_amount || payment.amount);
        const msg = (waSettings.deeplink_msg_payment || 'Dear {customer_name}, your payment of ${amount} has been received. Thank you!')
            .replace('{customer_name}', payment.customer_name || '')
            .replace('{amount}', parseFloat(amount || 0).toFixed(2));
        return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    };

    const [filters, setFilters] = useState({
        customer_id: '',
        status: 'unpaid',
        start_date: '',
        end_date: '',
        paid_date_start: '',
        paid_date_end: '',
        search_query: '',
        collected_by: '',
        collected_date: '',
        sort_by: 'billed_date',
        sort_desc: 'true'
    });

    const [collectors, setCollectors] = useState([]);
    useEffect(() => {
        apiService.fetchUsers().then(res => {
            const users = res.data || [];
            setCollectors(users.filter(u => u.role && (u.role.includes('collector') || u.role.includes('cashier'))));
        }).catch(err => console.error("Error fetching users", err));
    }, [apiService]);

    const [newPayment, setNewPayment] = useState({
        customer_id: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        paid: false,
        pre_payment: false,
        reason: ''
    });

    const [customerBalance, setCustomerBalance] = useState({
        unpaid_balance: 0,
        pre_payment_balance: 0,
        total_balance: 0,
        stored_balance: 0
    });

    const [showAddPaymentForm, setShowAddPaymentForm] = useState(false);
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [receiptData, setReceiptData] = useState(null);
    const [selectedPayment, setSelectedPayment] = useState(null);

    // --- This is the required state for the combined unpaid statement ---
    const [showCombinedReceiptModal, setShowCombinedReceiptModal] = useState(false);
    const [combinedReceiptData, setCombinedReceiptData] = useState(null);

    // --- NEW: State for the generate future payments modal ---
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [generateOptions, setGenerateOptions] = useState({
        customer_id: 'all',
        until: 'end_of_current_month'
    });

    // Helper Functions
    const getStatusColor = (isPaid) => {
        return isPaid ? theme.palette.success.main : theme.palette.error.main;
    };

    const getPaymentTypeColor = (isPrePayment) => {
        return isPrePayment ? theme.palette.secondary.main : theme.palette.primary.main;
    };

    // API Functions wrapped in useCallback for stability
    const fetchCustomersForDropdown = useCallback(async () => {
        try {
            const response = await apiService.fetchCustomers();
            setCustomers(response.customers || []);
        } catch (error) {
            console.error("Error fetching customers for payments view:", error);
            setSnackbar({ open: true, message: 'Failed to load customers for payment entry.', severity: 'error' });
        }
    }, [apiService, setSnackbar]);

    const fetchPayments = useCallback(async () => {
        setLoading(true);
        try {
            const params = { ...filters };
            if (currentTab === 1) { // If on 'Unpaid' tab, force status to unpaid
                params.status = 'unpaid';
            } /*else { // If on 'All Payments' tab, clear status filter
                delete params.status;
            }
            */

            const response = await apiService.fetchPayments(
                params.customer_id,
                params.status || '',
                params.start_date,
                params.end_date,
                params.search_query,
                params.collected_by,
                params.collected_date,
                params.sort_by,
                params.sort_desc,
                params.paid_date_start,
                params.paid_date_end
            );
            setPayments(response.data.payments || []); // FIX: Access the 'payments' key from the response
            setPaymentTotals(response.data.totals || null);
        } catch (error) {
            console.error("Error fetching payments:", error);
            setSnackbar({ open: true, message: 'Failed to load payments.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [filters, currentTab, apiService, setSnackbar]);

    const fetchCustomerBalance = useCallback(async (customerId) => {
        try {
            const response = await apiService.fetchCustomerBalance(customerId);
            setCustomerBalance(response.data);
        } catch (error) {
            console.error("Error fetching customer balance:", error);
            setSnackbar({ open: true, message: 'Failed to load customer balance.', severity: 'error' });
        }
    }, [apiService, setSnackbar]);

    // Initial Load & Filter Effects
    useEffect(() => {
        fetchCustomersForDropdown();
    }, [fetchCustomersForDropdown]);

    useEffect(() => {
        fetchPayments();
        if (filters.customer_id) {
            fetchCustomerBalance(filters.customer_id);
        }
    }, [filters, currentTab, fetchPayments, fetchCustomerBalance]);


    // Event Handlers
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const handleSearchChange = useCallback((value) => {
        setFilters(prev => ({ ...prev, search_query: value }));
    }, []);

    // --- NEW: Handler for generating future payments ---
    const handleGenerateFuturePayments = async () => {
        const today = new Date();
        let untilDate;

        if (generateOptions.until === 'end_of_current_month') {
            untilDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        } else { // end_of_next_month
            untilDate = new Date(today.getFullYear(), today.getMonth() + 2, 0);
        }

        const payload = {
            customer_id: generateOptions.customer_id,
            until_date: untilDate.toISOString().split('T')[0]
        };

        try {
            const response = await apiService.generateFuturePayments(payload);
            setSnackbar({ open: true, message: response.data.message, severity: 'success' });
            setShowGenerateModal(false);
            fetchPayments(); // Refresh the list to show new payments
        } catch (error) {
            console.error("Error generating future payments:", error);
            setSnackbar({ open: true, message: 'Failed to generate future payments. ' + (error.response?.data?.error || error.message), severity: 'error' });
        }
    };

    const handleAddPayment = async () => {
        if (!newPayment.customer_id || !newPayment.amount || !newPayment.date || !newPayment.reason) {
            setSnackbar({ open: true, message: 'Please fill out all required fields, including the reason.', severity: 'warning' });
            return;
        }

        const parsedAmount = parseFloat(newPayment.amount);
        if (isNaN(parsedAmount) || parsedAmount <= 0) {
            setSnackbar({ open: true, message: 'Amount must be a positive number.', severity: 'warning' });
            return;
        }
        setLoading(true);
        try {
            const paymentData = {
                ...newPayment,
                amount: parsedAmount,
                pre_payment: newPayment.pre_payment, // Assuming manual payments are pre-payments
                paid: newPayment.pre_payment,
                reason: newPayment.reason
            };

            await apiService.addCustomerPayment(paymentData);
            setSnackbar({ open: true, message: 'Payment added successfully!', severity: 'success' });
            setNewPayment({
                customer_id: '',
                amount: '',
                date: new Date().toISOString().split('T')[0],
                paid: false,
                pre_payment: false,
                reason: ''
            });
            setShowAddPaymentForm(false);
            fetchPayments();
            if (paymentData.customer_id) {
                fetchCustomerBalance(paymentData.customer_id);
            }
        } catch (error) {
            console.error('Error adding payment:', error);
            setSnackbar({ open: true, message: 'An error occurred while adding the payment. ' + (error.response?.data?.error || error.message), severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // --- Lost-credit review (admin/finance) ---
    const canReviewCredit = userRoles.includes('admin') || userRoles.includes('finance');
    const refreshCreditReviewCount = useCallback(async () => {
        try {
            const res = await apiService.getCreditReview();
            setCreditReviewCount(res.data.count || 0);
        } catch (e) { /* the button just stays hidden */ }
    }, [apiService]);
    useEffect(() => { if (canReviewCredit) refreshCreditReviewCount(); }, [canReviewCredit, refreshCreditReviewCount]);

    // --- Open the mark-as-paid dialog ---
    const openMarkPaidDialog = (payment) => {
        const otherCollected = payments.filter(p => p.customer_id === payment.customer_id && p.id !== payment.id
            && p.collected && !p.paid && !p.pre_payment);
        setMarkPaidDialog({
            open: true, paymentId: payment.id, outstanding: payment.amount, customerName: payment.customer_name,
            collectedAmount: payment.collected_amount, collecting: !payment.collected, customerId: payment.customer_id,
            otherCollectedTotal: otherCollected.reduce((t, p) => t + (p.collected_amount ?? p.amount), 0),
            otherCollectedCount: otherCollected.length,
        });
        setConfirmAllCollected(true);
        setMarkPaidMethod('cash');
        setMarkPaidReference('');
        const defaultAmount = (payment.collected && payment.collected_amount) ? payment.collected_amount : payment.amount;
        setMarkPaidAmount(String(defaultAmount));
    };

    const handleMarkPaid = async (paymentId, currentOutstandingAmount) => {
        if (markPaidSubmitting) return; // guard against a double-click firing this twice
        const amountInput = markPaidAmount;
        const paymentAmountInput = amountInput !== '' ? amountInput : null;
        const amountReceived = parseFloat(paymentAmountInput);

        if (isNaN(amountReceived) || amountReceived <= 0) {
            setSnackbar({ open: true, message: 'Please enter a valid positive amount.', severity: 'warning' });
            return;
        }
        setMarkPaidSubmitting(true);
        const dialog = markPaidDialog;
        setMarkPaidDialog({ open: false, paymentId: null, outstanding: 0, customerName: '' });

        const targetPayment = payments.find(p => p.id === paymentId);
        let action = 'pay';
        if (targetPayment && !targetPayment.collected && userRoles.includes('collector') && !userRoles.includes('admin') && !userRoles.includes('finance')) {
            action = 'collect';
        } else if (targetPayment && !targetPayment.collected && (userRoles.includes('admin') || userRoles.includes('finance'))) {
            // Admin can bypass collect and directly pay, or maybe we just do pay.
            action = 'collect'; // Actually, if it's not collected, and admin clicks 'collect', they collect it. Or they can directly mark it paid. The button says 'Collect' for them too if it's not collected. Let's send 'collect'.
            // Wait, let's look at the button label: `payment.collected ? 'Confirm Receipt' : 'Collect'`.
            // So if `!payment.collected` it is ALWAYS 'collect'.
            action = 'collect';
        }

        let payload = { action };
        if (action === 'collect') {
            payload.method = markPaidMethod;
            if (markPaidMethod === 'whish_transfer' && markPaidReference.trim()) payload.reference = markPaidReference.trim();
        }
        if (amountReceived < currentOutstandingAmount) {
            payload.partial_payment = true;
            payload.partial_amount = amountReceived;
        } else {
            payload.partial_payment = false;
            payload.partial_amount = amountReceived;
        }

        try {
            let response;
            if (!dialog.collecting && confirmAllCollected && dialog.otherCollectedCount > 0) {
                // Confirm every collected card of this customer in one go.
                response = await apiService.confirmCollectedPayments(dialog.customerId);
            } else if (amountReceived > currentOutstandingAmount + 0.004 && dialog.customerId) {
                // More than this card: the rest goes to the customer's other bills (then credit).
                response = await apiService.receiveCustomerPayment(dialog.customerId, {
                    amount: amountReceived,
                    action,
                    first_payment_id: paymentId,
                    ...(action === 'collect' ? { method: markPaidMethod } : {}),
                    ...(action === 'collect' && markPaidMethod === 'whish_transfer' && markPaidReference.trim()
                        ? { reference: markPaidReference.trim() } : {}),
                });
            } else {
                response = await apiService.markPaymentAsPaid(paymentId, payload);
            }
            setSnackbar({ open: true, message: response.data.message, severity: 'success' });
            if (canReviewCredit) refreshCreditReviewCount();

            fetchPayments();
            if (filters.customer_id) {
                fetchCustomerBalance(filters.customer_id);
            }
            const paidPayment    = payments.find(p => p.id === paymentId) || targetPayment || {};
            const paidCustomer   = customers.find(c => c.id === paidPayment.customer_id) || {};
            const paidCustomerPhone = paidPayment.customer_phone || paidCustomer.phone || '';
            const paidCustomerName  = paidPayment.customer_name || paidCustomer.name || '';

            // ── Auto-open WhatsApp deep link (deep-link mode) ──────────────────
            if (waSettings.enabled && waSettings.mode === 'deeplink') {
                const phone = paidCustomerPhone.replace(/\D/g, '');
                if (phone) {
                    const amountPaid = parseFloat(
                        response.data?.amount_received_in_this_transaction ??
                        (action === 'collect' ? amountReceived : paidPayment.amount) ??
                        amountReceived ?? 0
                    ).toFixed(2);
                    const msg = (waSettings.deeplink_msg_payment || 'Dear {customer_name}, your payment of ${amount} has been received. Thank you!')
                        .replace('{customer_name}', paidCustomerName)
                        .replace('{amount}', amountPaid);
                    const waUrl = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;

                    try {
                        window.open(waUrl, '_blank', 'noopener,noreferrer');
                    } catch (e) {
                        console.warn('Popup blocked, use snackbar link:', e);
                    }

                    setSnackbar({
                        open: true,
                        message: `${response.data.message || 'Payment recorded!'} — WhatsApp link ready`,
                        severity: 'success',
                        action: (
                            <Button color="inherit" size="small" onClick={() => window.open(waUrl, '_blank', 'noopener,noreferrer')} sx={{ fontWeight: 700, textDecoration: 'underline' }}>
                                Open WhatsApp
                            </Button>
                        )
                    });
                    return;
                }
            }
            // ──────────────────────────────────────────────────────────────────

        } catch (error) {
            console.error("Error marking payment paid:", error);
            setSnackbar({ open: true, message: 'Failed to mark payment as paid. ' + (error.response?.data?.message || error.response?.data?.error || error.message), severity: 'error' });
        } finally {
            setMarkPaidSubmitting(false);
        }
    };

    // --- Open the mark-as-gratis dialog ---
    const openMarkGratisDialog = (payment) => {
        setMarkGratisDialog({ open: true, paymentId: payment.id, outstanding: payment.amount, customerName: payment.customer_name });
        setMarkGratisNote('');
    };

    const handleMarkGratis = async () => {
        if (markGratisSubmitting) return;
        const { paymentId } = markGratisDialog;
        setMarkGratisSubmitting(true);
        setMarkGratisDialog({ open: false, paymentId: null, outstanding: 0, customerName: '' });
        try {
            const response = await apiService.markPaymentGratis(paymentId, markGratisNote);
            setSnackbar({ open: true, message: response.data.message, severity: 'success' });
            fetchPayments();
            if (filters.customer_id) {
                fetchCustomerBalance(filters.customer_id);
            }
        } catch (error) {
            console.error("Error marking payment gratis:", error);
            setSnackbar({ open: true, message: 'Failed to mark payment gratis. ' + (error.response?.data?.error || error.response?.data?.message || error.message), severity: 'error' });
        } finally {
            setMarkGratisSubmitting(false);
        }
    };

    // --- Open the revert-payment dialog ---
    const openMethodDialog = (payment) => {
        setMethodDialog({
            open: true, payment,
            method: payment.collected_via === 'whish_transfer' ? 'whish_transfer' : 'cash',
            reference: payment.collected_via === 'whish_transfer' ? (payment.whish_transaction_number || '') : '',
        });
    };

    const handleSaveMethod = async () => {
        if (methodSubmitting || !methodDialog.payment) return;
        setMethodSubmitting(true);
        try {
            await apiService.setPaymentMethod(methodDialog.payment.id, methodDialog.method,
                methodDialog.method === 'whish_transfer' ? methodDialog.reference : '');
            setSnackbar({ open: true, message: methodDialog.method === 'whish_transfer' ? 'Marked as Whish transfer.' : 'Marked as cash.', severity: 'success' });
            setMethodDialog({ open: false, payment: null, method: 'cash', reference: '' });
            fetchPayments();
        } catch (error) {
            setSnackbar({ open: true, message: error.response?.data?.error || error.response?.data?.message || 'Failed to change payment method.', severity: 'error' });
        } finally {
            setMethodSubmitting(false);
        }
    };

    const openRevertDialog = (payment) => {
        setRevertDialog({ open: true, paymentId: payment.id, outstanding: payment.amount, customerName: payment.customer_name });
        setRevertReason('');
    };

    const handleRevertPayment = async () => {
        if (revertSubmitting) return;
        const { paymentId } = revertDialog;
        if (!revertReason.trim()) {
            setSnackbar({ open: true, message: 'Please enter a reason for reverting this payment.', severity: 'error' });
            return;
        }
        setRevertSubmitting(true);
        try {
            const response = await apiService.revertPayment(paymentId, revertReason.trim());
            setSnackbar({ open: true, message: response.data.message, severity: 'success' });
            setRevertDialog({ open: false, paymentId: null, outstanding: 0, customerName: '' });
            fetchPayments();
            if (filters.customer_id) {
                fetchCustomerBalance(filters.customer_id);
            }
        } catch (error) {
            console.error("Error reverting payment:", error);
            setSnackbar({ open: true, message: 'Failed to revert payment. ' + (error.response?.data?.error || error.response?.data?.message || error.message), severity: 'error' });
        } finally {
            setRevertSubmitting(false);
        }
    };


    const handleDeletePayment = async (paymentId) => {
        if (window.confirm('Are you sure you want to delete this payment? This action cannot be undone.')) {
            try {
                await apiService.deletePayment(paymentId);
                setSnackbar({ open: true, message: 'Payment deleted successfully!', severity: 'success' });
                fetchPayments();
                if (filters.customer_id) {
                    fetchCustomerBalance(filters.customer_id);
                }
            } catch (error) {
                console.error("Error deleting payment:", error);
                setSnackbar({ open: true, message: 'Failed to delete payment. ' + (error.response?.data?.error || error.message), severity: 'error' });
            }
        }
    };

    // --- Bulk delete selected payments ---
    // Fires ONE bulk request for the whole selection (instead of one HTTP call
    // per row) and guards against a double-click re-triggering the batch.
    const handleBulkDelete = async () => {
        if (bulkActionLoading || selected.length === 0) return;
        if (!window.confirm(`Delete ${selected.length} selected payment(s)? This cannot be undone.`)) return;
        setBulkActionLoading(true);
        try {
            const { data } = await apiService.bulkDeletePayments(selected);
            const failCount = (data.failed || []).length;
            setSnackbar({
                open: true,
                message: `${(data.succeeded || []).length} payment(s) deleted.${failCount ? ` Failed for ${failCount}.` : ''}`,
                severity: failCount ? 'warning' : 'success'
            });
            setSelected([]);
            fetchPayments();
            if (filters.customer_id) fetchCustomerBalance(filters.customer_id);
        } catch (error) {
            setSnackbar({ open: true, message: 'Bulk delete failed: ' + (error.response?.data?.error || error.response?.data?.message || error.message), severity: 'error' });
        } finally {
            setBulkActionLoading(false);
        }
    };

    // --- Bulk mark selected payments as paid ---
    const handleBulkMarkPaid = async () => {
        if (bulkActionLoading || selected.length === 0) return;
        if (!window.confirm(`Mark ${selected.length} selected payment(s) as paid?`)) return;
        setBulkActionLoading(true);
        try {
            const { data } = await apiService.bulkMarkPaymentsPaid(selected);
            const failCount = (data.failed || []).length;
            setSnackbar({
                open: true,
                message: `${(data.succeeded || []).length} payment(s) marked as paid.${failCount ? ` Failed for ${failCount}.` : ''}`,
                severity: failCount ? 'warning' : 'success'
            });
            setSelected([]);
            fetchPayments();
            if (filters.customer_id) fetchCustomerBalance(filters.customer_id);
        } catch (error) {
            setSnackbar({ open: true, message: 'Bulk mark paid failed: ' + (error.response?.data?.error || error.response?.data?.message || error.message), severity: 'error' });
        } finally {
            setBulkActionLoading(false);
        }
    };

    // --- Selection helpers ---
    const handleSelectAll = (e) => {
        if (e.target.checked) { setSelected(payments.map(p => p.id)); }
        else { setSelected([]); }
    };
    const handleSelectOne = (id) => {
        setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };
    const handlePrepareReceipt = async (paymentId) => {
        try {
            const response = await apiService.fetchReceipt(paymentId);
            const data = response.data;
            const customerId = (payments.find(p => p.id === paymentId) || {}).customer_id;

            if (customerId) {
                const balanceResponse = await apiService.fetchCustomerBalance(customerId);
                data.customer_new_balance = balanceResponse.data.stored_balance;
            } else {
                data.customer_new_balance = 0;
            }

            data.amount_paid_now = data.amount_on_record;
            data.remaining_on_payment = data.paid_status !== 'Paid' ? data.amount_on_record : 0;

            setReceiptData(data);
            setShowReceiptModal(true);
        } catch (error) {
            console.error("Error fetching receipt:", error);
            setSnackbar({ open: true, message: 'Failed to fetch receipt. ' + (error.response?.data?.error || error.response?.data?.message || error.message), severity: 'error' });
        }
    };

const handlePrint = () => {
    if (!receiptData) return;

    const arabicMonths = [
        'كانون الثاني', 'شباط', 'آذار', 'نيسان', 'أيار', 'حزيران',
        'تموز', 'آب', 'أيلول', 'تشرين الأول', 'تشرين الثاني', 'كانون الأول'
    ];
    const date = new Date(receiptData.payment_date);
    const month = date.getMonth();
    const year = date.getFullYear();
    const arabicMonthYear = `${arabicMonths[month]} ${year}`;
    const currentBalance = (parseFloat(receiptData.customer_new_balance) || 0).toFixed(2);
    const previousBalance = ((parseFloat(receiptData.customer_new_balance) || 0) + (parseFloat(receiptData.amount_paid_now) || 0)).toFixed(2);

    const winPrint = window.open('', '', 'left=0,top=0,width=900,height=400,toolbar=0,scrollbars=0,status=0');
    winPrint.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Receipt</title>
                <style>
                    @page {
                        size: 21cm 30cm;
                        margin: 0;
                    }
                    body {
                        margin: 0;
                        font-family: Arial, sans-serif;
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                    }
                    .receipt-container {
                        width: 21cm;
                        height: 7.5cm;
                        display: flex !important;
                        direction: rtl;
                        background-color: white;
                        padding-top: 40px;
                        box-sizing: border-box;
                    }
                    /* Force page break after every 4 receipts */
                    .receipt-container:nth-child(4n) {
                        page-break-after: always;
                    }
                    .main-part {
                        width: 14.1cm;
                        padding: 40px 50px 10px 15px;
                        box-sizing: border-box;
                    }
                    .mini-part {
                        width: 6.9cm;
                        padding: 40px 50px 10px 15px;
                        box-sizing: border-box;
                        border-right: 1px dashed #000;
                    }
                    .receipt-details span {
                        display: block;
                        font-size: 16px;
                        margin-bottom: 2px;
                        color: #000;
                    }
                </style>
            </head>
            <body>
                <div class="receipt-container">
                    <div class="main-part">
                        <div class="receipt-details">
                            <span>الإسم: ${escapeHtml(receiptData.customer_name)}</span>
                            <span>العنوان: ${escapeHtml(receiptData.customer_address)}</span>
                            <span>الهاتف: ${escapeHtml(receiptData.customer_phone)}</span>
                            <span>تاريخ الإيصال: ${escapeHtml(receiptData.payment_date)}</span>
                            <span>الدفعة الشهرية: ${(parseFloat(receiptData.subscription_plan_details?.price) || 0).toFixed(2)}$ - الخدمة: ${escapeHtml(receiptData.subscription_plan_details?.name || 'انترنت')}</span>
                            <span>عن شهر: ${arabicMonthYear}</span>
                            <span style="font-weight: bold;">الرصيد الحالي: ${currentBalance}$ - الرصيد السابق: ${previousBalance}$</span>
                        </div>
                    </div>
                    <div class="mini-part">
                        <div class="receipt-details">
                            <span>الإسم: ${escapeHtml(receiptData.customer_name)}</span>
                            <span>العنوان: ${escapeHtml(receiptData.customer_address)}</span>
                            <span>الهاتف: ${escapeHtml(receiptData.customer_phone)}</span>
                            <span>الدفعة الشهرية: ${(parseFloat(receiptData.subscription_plan_details?.price) || 0).toFixed(2)}$</span>
                            <span>الرصيد الحالي: ${currentBalance}$</span>
                            <span>الرصيد السابق: ${previousBalance}$</span>
                        </div>
                    </div>
                </div>
            </body>
            </html>
        `);
    winPrint.document.close();
    winPrint.focus();

    setTimeout(() => {
        winPrint.print();
        winPrint.close();
    }, 250);
    };

    const handlePrintReceipt = async (paymentId) => {
        try {
            const response = await apiService.fetchReceipt(paymentId);
            const data = response.data;

            const customerId = (payments.find(p => p.id === paymentId) || {}).customer_id;
            if (customerId) {
                const balanceResponse = await apiService.fetchCustomerBalance(customerId);
                data.customer_new_balance = balanceResponse.data.stored_balance;
            } else {
                data.customer_new_balance = 0;
            }

            data.amount_paid_now = 0; // Not a new transaction
            data.remaining_on_payment = data.paid_status !== 'Paid' ? data.amount_on_record : 0;

            setReceiptData(data);
            setShowReceiptModal(true);
        } catch (error) {
            console.error("Error fetching receipt:", error);
            setSnackbar({ open: true, message: 'Failed to fetch receipt. ' + (error.response?.data?.error || error.response?.data?.message || error.message), severity: 'error' });
        }
    };

    // --- NEW: Handler for printing the combined unpaid statement ---
    const handlePrintUnpaidStatement = async () => {
        if (!filters.customer_id) {
            setSnackbar({ open: true, message: 'Please select a customer first.', severity: 'warning' });
            return;
        }
        try {
            // NOTE: You must add `fetchUnpaidReceipt` to your apiService to call the new endpoint.
            // Example: fetchUnpaidReceipt: (customerId) => api.get(`/customers/${customerId}/unpaid_receipt`),
            const response = await apiService.fetchUnpaidReceipt(filters.customer_id);
            setCombinedReceiptData(response.data);
            setShowCombinedReceiptModal(true);
        } catch (error) {
            console.error("Error fetching unpaid statement:", error);
            setSnackbar({ open: true, message: 'Failed to fetch unpaid statement. ' + (error.response?.data?.error || error.response?.data?.message || error.message), severity: 'error' });
        }
    };

    
    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };
/*
    const filteredPayments = (Array.isArray(payments) ? payments : []).filter(payment => {
        const matchesCustomer = filters.customer_id ? payment.customer_id === parseInt(filters.customer_id) : true;
        const matchesStatus = (currentTab === 1) ? !payment.paid : true;
        const matchesStartDate = filters.start_date ? new Date(payment.date) >= new Date(filters.start_date) : true;
        const matchesEndDate = filters.end_date ? new Date(payment.date) <= new Date(filters.end_date) : true;
        const matchesSearch = filters.search_query ? (payment.customer_name && payment.customer_name.toLowerCase().includes(filters.search_query.toLowerCase())) : true;
        return matchesCustomer && matchesStatus && matchesStartDate && matchesEndDate && matchesSearch;
    });
    */

    const totalRevenue = React.useMemo(() => getTotalRevenue(payments), [payments]);
    const openRefundDialog = (payment) => {
        setRefundDialog({
            open: true, payment,
            amount: String(payment.amount ?? ''),
            paidVia: defaultRefundVia(payment),
            reason: '',
        });
    };

    const closeRefundDialog = () => setRefundDialog(EMPTY_REFUND);

    const refundAmountError = (() => {
        if (!refundDialog.payment) return '';
        const amt = parseFloat(refundDialog.amount);
        if (!(amt > 0)) return 'Enter an amount above 0.';
        if (amt > (parseFloat(refundDialog.payment.amount) || 0) + 1e-9) return 'Cannot exceed the original payment.';
        return '';
    })();

    const handleRefundPayment = async () => {
        if (refundSubmitting || !refundDialog.payment || refundAmountError || !refundDialog.reason.trim()) return;
        setRefundSubmitting(true);
        try {
            const response = await apiService.refundPayment(refundDialog.payment.id, {
                amount: parseFloat(refundDialog.amount),
                reason: refundDialog.reason.trim(),
                paid_via: refundDialog.paidVia,
            });
            setSnackbar({ open: true, message: `${response.data.message} Paid out via ${refundDialog.paidVia === 'whish' ? 'Whish' : 'cash'}.`, severity: 'success' });
            closeRefundDialog();
            fetchPayments();
            if (filters.customer_id) {
                fetchCustomerBalance(filters.customer_id);
            }
        } catch (error) {
            console.error("Error issuing refund:", error);
            setSnackbar({ open: true, message: 'Failed to issue refund. ' + (error.response?.data?.error || error.response?.data?.message || error.message), severity: 'error' });
        } finally {
            setRefundSubmitting(false);
        }
    };

    const currentMonthRevenue = React.useMemo(() => getCurrentMonthRevenue(payments), [payments]);

    const isAdminOrFinanceRole = userRoles.includes('admin') || userRoles.includes('finance');
    const canCollectRole = isAdminOrFinanceRole || userRoles.includes('collector');
    const latestRowHandlersRef = useRef(null);
    latestRowHandlersRef.current = {
        handleSelectOne, openMarkPaidDialog, openMarkGratisDialog, handlePrepareReceipt,
        buildWhatsAppLink, openRevertDialog, openMethodDialog, openRefundDialog, handleDeletePayment,
    };
    const rowActions = React.useMemo(() => {
        const h = () => latestRowHandlersRef.current;
        return {
            handleSelectOne: (...a) => h().handleSelectOne(...a),
            openMarkPaidDialog: (...a) => h().openMarkPaidDialog(...a),
            openMarkGratisDialog: (...a) => h().openMarkGratisDialog(...a),
            handlePrepareReceipt: (...a) => h().handlePrepareReceipt(...a),
            buildWhatsAppLink: (...a) => h().buildWhatsAppLink(...a),
            openRevertDialog: (...a) => h().openRevertDialog(...a),
            openMethodDialog: (...a) => h().openMethodDialog(...a),
            openRefundDialog: (...a) => h().openRefundDialog(...a),
            handleDeletePayment: (...a) => h().handleDeletePayment(...a),
        };
    }, []);

    // Virtualized rendering (see useWindowRows): the page lists every
    // matching payment with no paging, so a few hundred unpaid payments used
    // to mean a few hundred mounted cards/rows. Grid rows follow the old
    // Grid breakpoints (1 column, 2 from sm, 3 from lg).
    const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
    const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
    const paymentGridColumns = isLgUp ? 3 : isSmUp ? 2 : 1;
    const paymentGridRows = React.useMemo(() => chunkRows(payments, paymentGridColumns), [payments, paymentGridColumns]);
    const isPaymentGrid = viewMode === 'grid';
    const paymentRowKey = useCallback((i) => (isPaymentGrid
        ? `g${paymentGridColumns}:${paymentGridRows[i]?.[0]?.id ?? i}`
        : `l:${payments[i]?.id ?? i}`), [isPaymentGrid, paymentGridColumns, paymentGridRows, payments]);
    const listRows = useWindowRows(isPaymentGrid ? paymentGridRows.length : payments.length, isPaymentGrid ? 330 : 64, paymentRowKey);

    // PaymentCard wraps the module-level _PaymentCard so React.memo works properly.
    // Handlers are collected into one stable object to minimise re-renders.
    const cardHandlers = React.useMemo(() => ({
        getStatusColor,
        getPaymentTypeColor,
        // Through the ref-backed wrapper: reads latest closures/state, not render 0's.
        openMarkPaidDialog: rowActions.openMarkPaidDialog,
        openMarkGratisDialog: rowActions.openMarkGratisDialog,
        openRevertDialog: rowActions.openRevertDialog,
        openMethodDialog: rowActions.openMethodDialog,
        openRefundDialog: rowActions.openRefundDialog,
        handlePrepareReceipt: rowActions.handlePrepareReceipt,
        handleDeletePayment: rowActions.handleDeletePayment,
        buildWhatsAppLink: rowActions.buildWhatsAppLink,
        waSettings,
        userRoles,
        twsEnabled,
        handleSendPaymentLink,
        sendLinkLoading,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [waSettings, userRoles.join(','), twsEnabled, sendLinkLoading]);

    const PaymentCard = React.useCallback(
        ({ payment }) => <PaymentCardItem payment={payment} {...cardHandlers} />,
        [cardHandlers]
    );

    return (
        <Box sx={{ p: 3, background: 'linear-gradient(135deg, #f6f9fc 0%, #ffffff 100%)', minHeight: '100vh' }}>
            {/* Header Section */}
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, sm: 3, md: 4 },
                    mb: 4,
                    borderRadius: '24px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: -50,
                        right: -50,
                        width: 200,
                        height: 200,
                        borderRadius: '50%',
                        background: alpha('#ffffff', 0.1),
                        filter: 'blur(1px)'
                    }}
                />
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'flex-start' }, gap: { xs: 2, sm: 0 }, mb: 3 }}>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.3rem', sm: '1.75rem', md: '2.125rem' } }}>
                                Payments Management
                            </Typography>
                            <Typography variant="body1" sx={{ opacity: 0.9, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                                Track and manage customer payments efficiently
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 }, width: { xs: '100%', sm: 'auto' } }}>
                            {canReviewCredit && creditReviewCount > 0 && (
                                <Button variant="contained" color="warning" onClick={() => setCreditReviewOpen(true)}
                                    sx={{ borderRadius: '16px', textTransform: 'none', fontWeight: 600, px: { xs: 2, sm: 3 }, py: { xs: 1, sm: 1.5 }, width: { xs: '100%', sm: 'auto' } }}>
                                    Credit check ({creditReviewCount})
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                startIcon={<ScheduleSendIcon />}
                                onClick={() => setShowGenerateModal(true)}
                                sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    color: 'white',
                                    borderRadius: '16px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: { xs: 2, sm: 3 },
                                    py: { xs: 1, sm: 1.5 },
                                    width: { xs: '100%', sm: 'auto' },
                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)', transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(0,0,0,0.2)' },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Generate Future Payments
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setShowAddPaymentForm(true)}
                                sx={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    backdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    color: 'white',
                                    borderRadius: '16px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: { xs: 2, sm: 3 },
                                    py: { xs: 1, sm: 1.5 },
                                    width: { xs: '100%', sm: 'auto' },
                                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.3)', transform: 'translateY(-2px)', boxShadow: '0 8px 20px rgba(0,0,0,0.2)' },
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                Add New Payment
                            </Button>
                        </Box>
                    </Box>

                    {/* Statistics */}
                    <Box sx={{ display: 'flex', gap: { xs: 2, sm: 3 }, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PaymentIcon sx={{ fontSize: 20 }} />
                            <Box>
                                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                                    Total Payments
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {payments.length}
                                </Typography>
                            </Box>
                        </Box>
                        <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CheckCircleIcon sx={{ fontSize: 20 }} />
                            <Box>
                                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                                    Paid Payments
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    {payments.filter(p => p.paid && !p.is_refund).length}
                                </Typography>
                            </Box>
                        </Box>
                        <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUpIcon sx={{ fontSize: 20 }} />
                            <Box>
                                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                                    Current Month Revenue
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    ${currentMonthRevenue.toFixed(2)}
                                </Typography>
                            </Box>
                        </Box>
                        <Divider orientation="vertical" flexItem sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TrendingUpIcon sx={{ fontSize: 20 }} />
                            <Box>
                                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                                    Total Revenue
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                    ${totalRevenue.toFixed(2)}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Paper>

            {/* View Toggle + Bulk Actions Bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <ToggleButtonGroup value={viewMode} exclusive onChange={(e, v) => v && setViewMode(v)} size="small">
                    <ToggleButton value="grid"><ViewModuleIcon /></ToggleButton>
                    <ToggleButton value="list"><ViewListIcon /></ToggleButton>
                </ToggleButtonGroup>
                {selected.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{selected.length} selected</Typography>
                        {(userRoles.includes('admin') || userRoles.includes('finance')) && (
                            <Button size="small" variant="contained" color="success" startIcon={bulkActionLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />} onClick={handleBulkMarkPaid} disabled={bulkActionLoading}>Mark Paid</Button>
                        )}
                        {(userRoles.includes('admin') || userRoles.includes('finance')) && (
                            <Button size="small" variant="contained" color="error" startIcon={bulkActionLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />} onClick={handleBulkDelete} disabled={bulkActionLoading}>Delete Selected</Button>
                        )}
                        <Button size="small" variant="outlined" onClick={() => setSelected([])} disabled={bulkActionLoading}>Clear</Button>
                    </Box>
                )}
            </Box>

            {/* Filters Section */}
            <Paper
                sx={{
                    p: 3,
                    borderRadius: '16px',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    mb: 3
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <FilterListIcon sx={{ color: 'text.secondary' }} />
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Filters & Search
                    </Typography>
                </Box>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            select
                            label="Customer"
                            name="customer_id"
                            value={filters.customer_id}
                            onChange={handleFilterChange}
                            size="small"
                        >
                            <MenuItem value="">All Customers</MenuItem>
                            {customers.map(customer => (
                                <MenuItem key={customer.id} value={customer.id}>{customer.name}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            select
                            label="Status"
                            name="status"
                            value={filters.status}
                            onChange={handleFilterChange}
                            size="small"
                        >
                            <MenuItem value="">All Statuses</MenuItem>
                            <MenuItem value="paid">Paid</MenuItem>
                            <MenuItem value="unpaid">Unpaid</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Billed Start Date"
                            name="start_date"
                            value={filters.start_date}
                            onChange={handleFilterChange}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Billed End Date"
                            name="end_date"
                            value={filters.end_date}
                            onChange={handleFilterChange}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Paid Start Date"
                            name="paid_date_start"
                            value={filters.paid_date_start}
                            onChange={handleFilterChange}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Paid End Date"
                            name="paid_date_end"
                            value={filters.paid_date_end}
                            onChange={handleFilterChange}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            select
                            fullWidth
                            label="Collector"
                            name="collected_by"
                            value={filters.collected_by}
                            onChange={handleFilterChange}
                            size="small"
                        >
                            <MenuItem value=""><em>All Collectors</em></MenuItem>
                            {collectors.map(c => (
                                <MenuItem key={c.id} value={c.id}>{c.username}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Collected Date"
                            name="collected_date"
                            value={filters.collected_date}
                            onChange={handleFilterChange}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <DebouncedSearchInput
                            fullWidth
                            label="Search by customer name"
                            value={filters.search_query || ''}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'text.secondary' }} /></InputAdornment>
                            }}
                            size="small"
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* Customer Balance Card */}
            {filters.customer_id && (
                <Paper sx={{ p: 3, borderRadius: '16px', background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)', color: 'white', mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                        Customer Balance Summary
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}><Box><Typography variant="caption" sx={{ opacity: 0.8 }}>Unpaid Balance</Typography><Typography variant="h6" sx={{ fontWeight: 700 }}>${(customerBalance.calculated_unpaid_balance || 0).toFixed(2)}</Typography></Box></Grid>
                        <Grid item xs={12} sm={3}><Box><Typography variant="caption" sx={{ opacity: 0.8 }}>Pre-Payment Balance</Typography><Typography variant="h6" sx={{ fontWeight: 700 }}>${(customerBalance.calculated_pre_payment_balance || 0).toFixed(2)}</Typography></Box></Grid>
                        <Grid item xs={12} sm={3}><Box><Typography variant="caption" sx={{ opacity: 0.8 }}>Account Balance</Typography><Typography variant="h6" sx={{ fontWeight: 700 }}>${(customerBalance.stored_balance || 0).toFixed(2)}</Typography><Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.7rem' }}>{customerBalance.stored_balance < 0 ? 'Customer owes' : 'Customer credit'}</Typography></Box></Grid>
                        {/* --- NEW: Button to print unpaid statement --- */}
                        <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                startIcon={<ReceiptIcon />}
                                onClick={handlePrintUnpaidStatement}
                                disabled={(customerBalance.calculated_unpaid_balance || 0) <= 0}
                                sx={{ mt: { xs: 2, sm: 0 }, backgroundColor: 'white', color: theme.palette.primary.main, '&:hover': { backgroundColor: alpha('#ffffff', 0.9) }, '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.5)' } }}
                            >
                                Print Statement
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Totals for whatever the filters above currently select */}
            <PaymentTotalsBar totals={paymentTotals} loading={loading} />

            {/* Payments List */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={40} thickness={4} />
                </Box>
            ) : payments.length === 0 ? (
                <Fade in timeout={800}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, textAlign: 'center' }}>
                        <Box sx={{ width: 120, height: 120, borderRadius: '50%', background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                            <PaymentIcon sx={{ fontSize: 48, color: theme.palette.primary.main, opacity: 0.7 }} />
                        </Box>
                        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>No payments found</Typography>
                        <Typography variant="body2" sx={{ color: 'text.disabled', mb: 3 }}>Adjust your filters or add a new payment to get started</Typography>
                    </Box>
                </Fade>
            ) : viewMode === 'grid' ? (
                <div ref={listRows.anchorRef}>
                    <div style={{ height: listRows.padTop }} />
                    {listRows.items.map((vr) => (
                        <Box
                            key={vr.key}
                            data-index={vr.index}
                            ref={listRows.virtualizer.measureElement}
                            sx={{ display: 'grid', gridTemplateColumns: `repeat(${paymentGridColumns}, minmax(0, 1fr))`, columnGap: '24px', pb: '8px', alignItems: 'start' }}
                        >
                            {(paymentGridRows[vr.index] || []).map((payment) => (
                                <PaymentCard key={payment.id} payment={payment} />
                            ))}
                        </Box>
                    ))}
                    <div style={{ height: listRows.padBottom }} />
                </div>
            ) : (
                // --- LIST VIEW ---
                <Paper sx={{ borderRadius: '16px', overflow: 'hidden' }}>
                    {selected.length > 0 && (
                        <Toolbar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                            <Typography sx={{ flex: 1, fontWeight: 600 }}>{selected.length} selected</Typography>
                            {(userRoles.includes('admin') || userRoles.includes('finance')) && (
                                <Button size="small" variant="contained" color="success" startIcon={bulkActionLoading ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />} onClick={handleBulkMarkPaid} disabled={bulkActionLoading} sx={{ mr: 1 }}>Mark Paid</Button>
                            )}
                            {(userRoles.includes('admin') || userRoles.includes('finance')) && (
                                <Button size="small" variant="contained" color="error" startIcon={bulkActionLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />} onClick={handleBulkDelete} disabled={bulkActionLoading}>Delete Selected</Button>
                            )}
                        </Toolbar>
                    )}
                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            indeterminate={selected.length > 0 && selected.length < payments.length}
                                            checked={payments.length > 0 && selected.length === payments.length}
                                            onChange={handleSelectAll}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Billed Date</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Paid Date</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody ref={listRows.anchorRef}>
                                {listRows.padTop > 0 && (
                                    <tr aria-hidden="true" style={{ height: listRows.padTop }}><td colSpan={8} style={{ padding: 0, border: 0 }} /></tr>
                                )}
                                {listRows.items.map((vr) => {
                                    const payment = payments[vr.index];
                                    if (!payment) return null;
                                    return (
                                        <PaymentListRow
                                            key={payment.id}
                                            payment={payment}
                                            index={vr.index}
                                            sel={selected.includes(payment.id)}
                                            canCollect={canCollectRole}
                                            isAdminOrFinance={isAdminOrFinanceRole}
                                            waSettings={waSettings}
                                            actions={rowActions}
                                            measureRef={listRows.virtualizer.measureElement}
                                        />
                                    );
                                })}
                                {listRows.padBottom > 0 && (
                                    <tr aria-hidden="true" style={{ height: listRows.padBottom }}><td colSpan={8} style={{ padding: 0, border: 0 }} /></tr>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}


            {/* Add Payment Dialog */}
            <Dialog open={showAddPaymentForm} onClose={() => setShowAddPaymentForm(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add New Payment</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={customers}
                                getOptionLabel={(option) => option.name || `ID: ${option.id}`}
                                value={customers.find(c => c.id === newPayment.customer_id) || null}
                                onChange={(e, newValue) => {
                                    setNewPayment({ ...newPayment, customer_id: newValue ? newValue.id : '' });
                                }}
                                renderInput={(params) => <TextField {...params} label="Customer" fullWidth variant="outlined" />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Amount"
                                value={newPayment.amount}
                                onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Date"
                                value={newPayment.date}
                                onChange={(e) => setNewPayment({ ...newPayment, date: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Reason"
                                required
                                value={newPayment.reason}
                                onChange={(e) => setNewPayment({ ...newPayment, reason: e.target.value })}
                            />
                        </Grid>
                        {/* A pre-payment is recorded as already received -- only
                            finance/admin may do that (add_payment refuses it for a cashier). */}
                        {(userRoles.includes('admin') || userRoles.includes('finance')) && (
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={newPayment.pre_payment}
                                            onChange={(e) => setNewPayment({ ...newPayment, pre_payment: e.target.checked })}
                                        />
                                    }
                                    label="Pre-Payment"
                                />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowAddPaymentForm(false)}>Cancel</Button>
                    <Button onClick={handleAddPayment} variant="contained">Add Payment</Button>
                </DialogActions>
            </Dialog>

            {/* Mark as Paid Dialog */}
            <CreditReviewDialog open={creditReviewOpen} onClose={() => setCreditReviewOpen(false)}
                onChanged={() => { refreshCreditReviewCount(); fetchPayments(); }} />

            <Dialog open={markPaidDialog.open} onClose={() => setMarkPaidDialog({ open: false, paymentId: null, outstanding: 0, customerName: '' })} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Update Payment Status</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Customer: <strong>{markPaidDialog.customerName}</strong><br />
                        Outstanding: <strong>${(markPaidDialog.outstanding || 0).toFixed(2)}</strong>
                    </Typography>
                    <TextField
                        fullWidth
                        autoFocus
                        type="number"
                        label="Amount Received"
                        value={markPaidAmount}
                        onChange={(e) => setMarkPaidAmount(e.target.value)}
                        InputProps={{ inputProps: { min: 0.01, step: 0.01 } }}
                        disabled={!markPaidDialog.collecting && confirmAllCollected && markPaidDialog.otherCollectedCount > 0}
                        helperText={(() => {
                            const v = parseFloat(markPaidAmount);
                            const extra = v - (markPaidDialog.outstanding || 0);
                            if (v < markPaidDialog.outstanding) return 'Partial payment — balance will be updated';
                            if (extra > 0.004) return `Full payment — the extra $${extra.toFixed(2)} goes to this customer's other unpaid bills`
                                + (markPaidDialog.collecting ? '' : ', then credit');
                            return 'Full payment';
                        })()}
                    />
                    {!markPaidDialog.collecting && markPaidDialog.otherCollectedCount > 0 && (
                        <FormControlLabel sx={{ mt: 1 }}
                            control={<Checkbox checked={confirmAllCollected} onChange={(e) => setConfirmAllCollected(e.target.checked)} />}
                            label={`Also confirm this customer's other ${markPaidDialog.otherCollectedCount} collected payment(s) ($${markPaidDialog.otherCollectedTotal.toFixed(2)})`} />
                    )}
                    {markPaidDialog.collecting && (
                        <>
                            <TextField select fullWidth sx={{ mt: 2 }} label="Paid by" value={markPaidMethod}
                                onChange={(e) => setMarkPaidMethod(e.target.value)}>
                                <MenuItem value="cash">Cash</MenuItem>
                                <MenuItem value="whish_transfer">Whish transfer (sent directly to our Whish account)</MenuItem>
                            </TextField>
                            {markPaidMethod === 'whish_transfer' && (
                                <TextField fullWidth sx={{ mt: 2 }} label="Whish reference (optional)" value={markPaidReference}
                                    inputProps={{ maxLength: 64 }} onChange={(e) => setMarkPaidReference(e.target.value)}
                                    helperText="Not counted as cash on the Daily Cash report" />
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMarkPaidDialog({ open: false, paymentId: null, outstanding: 0, customerName: '' })}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={markPaidSubmitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                        onClick={() => handleMarkPaid(markPaidDialog.paymentId, markPaidDialog.outstanding)}
                        disabled={markPaidSubmitting}
                    >
                        Confirm Payment
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={markGratisDialog.open} onClose={() => setMarkGratisDialog({ open: false, paymentId: null, outstanding: 0, customerName: '' })} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Mark Payment Gratis</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Customer: <strong>{markGratisDialog.customerName}</strong><br />
                        Original amount: <strong>${(markGratisDialog.outstanding || 0).toFixed(2)}</strong><br />
                        This waives the payment — no charge will be recorded and the customer's balance will not change.
                    </Typography>
                    <TextField
                        fullWidth
                        autoFocus
                        label="Reason (optional)"
                        value={markGratisNote}
                        onChange={(e) => setMarkGratisNote(e.target.value)}
                        placeholder="e.g. loyalty reward, outage compensation"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMarkGratisDialog({ open: false, paymentId: null, outstanding: 0, customerName: '' })}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="info"
                        startIcon={markGratisSubmitting ? <CircularProgress size={16} color="inherit" /> : <CardGiftcardIcon />}
                        onClick={handleMarkGratis}
                        disabled={markGratisSubmitting}
                    >
                        Confirm Gratis
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={methodDialog.open} onClose={() => !methodSubmitting && setMethodDialog({ open: false, payment: null, method: 'cash', reference: '' })} maxWidth="xs" fullWidth>
                <DialogTitle>Payment Method</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        How did {methodDialog.payment?.customer_name || 'the customer'} pay{methodDialog.payment ? ` $${(parseFloat(methodDialog.payment.amount) || 0).toFixed(2)}` : ''}?
                    </Typography>
                    <TextField select fullWidth label="Method" value={methodDialog.method}
                        onChange={(e) => setMethodDialog({ ...methodDialog, method: e.target.value })}>
                        <MenuItem value="cash">Cash</MenuItem>
                        <MenuItem value="whish_transfer">Whish transfer (sent directly to our Whish account)</MenuItem>
                    </TextField>
                    {methodDialog.method === 'whish_transfer' && (
                        <TextField fullWidth sx={{ mt: 2 }} label="Whish reference (optional)" value={methodDialog.reference}
                            inputProps={{ maxLength: 64 }}
                            onChange={(e) => setMethodDialog({ ...methodDialog, reference: e.target.value })}
                            helperText="Transaction number from the Whish app, if you have it" />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                        A Whish transfer is not counted as cash in the Daily Cash report and is listed in the Customer Whish Payments report.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setMethodDialog({ open: false, payment: null, method: 'cash', reference: '' })} disabled={methodSubmitting}>Cancel</Button>
                    <Button variant="contained" onClick={handleSaveMethod} disabled={methodSubmitting}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={revertDialog.open} onClose={() => !revertSubmitting && setRevertDialog({ open: false, paymentId: null, outstanding: 0, customerName: '' })} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Revert Payment to Pending</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Customer: <strong>{revertDialog.customerName}</strong><br />
                        Amount: <strong>${(revertDialog.outstanding || 0).toFixed(2)}</strong><br />
                        This undoes the payment: it goes back to pending, the amount is re-added to the customer's balance (if it wasn't gratis), and any collection record is cleared.
                    </Typography>
                    <TextField
                        fullWidth
                        autoFocus
                        required
                        label="Reason for reverting"
                        value={revertReason}
                        onChange={(e) => setRevertReason(e.target.value)}
                        placeholder="e.g. marked paid by mistake"
                        multiline
                        minRows={2}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevertDialog({ open: false, paymentId: null, outstanding: 0, customerName: '' })} disabled={revertSubmitting}>Cancel</Button>
                    <Button
                        variant="contained"
                        sx={{ bgcolor: '#F59E0B', '&:hover': { bgcolor: '#D97706' } }}
                        startIcon={revertSubmitting ? <CircularProgress size={16} color="inherit" /> : <UndoIcon />}
                        onClick={handleRevertPayment}
                        disabled={revertSubmitting || !revertReason.trim()}
                    >
                        Confirm Revert
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={refundDialog.open} onClose={() => !refundSubmitting && closeRefundDialog()} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Refund Payment</DialogTitle>
                <DialogContent>
                    {refundDialog.payment && (
                        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                            Customer: <strong>{refundDialog.payment.customer_name}</strong><br />
                            Original payment: <strong>{(parseFloat(refundDialog.payment.amount) || 0).toFixed(2)} {refundDialog.payment.currency || 'USD'}</strong>
                            {' '}({defaultRefundVia(refundDialog.payment) === 'whish' ? 'received via Whish' : 'received in cash'})<br />
                            The refund is recorded in the same currency and debited from the customer's balance.
                        </Typography>
                    )}
                    <TextField
                        fullWidth
                        type="number"
                        label={`Refund amount (${refundDialog.payment?.currency || 'USD'})`}
                        value={refundDialog.amount}
                        onChange={(e) => setRefundDialog({ ...refundDialog, amount: e.target.value })}
                        error={!!refundAmountError}
                        helperText={refundAmountError || 'Defaults to the full amount; lower it for a partial refund.'}
                        inputProps={{ min: 0, step: 'any' }}
                        sx={{ mb: 2 }}
                    />
                    <TextField select fullWidth label="Paid out via" value={refundDialog.paidVia} sx={{ mb: 2 }}
                        onChange={(e) => setRefundDialog({ ...refundDialog, paidVia: e.target.value })}
                        helperText="Which account the money left -- the refund shows under this channel in the Daily Cash flow.">
                        <MenuItem value="cash">Cash</MenuItem>
                        <MenuItem value="whish">Whish (sent to the customer's Whish)</MenuItem>
                    </TextField>
                    <TextField
                        fullWidth
                        required
                        label="Reason for refund"
                        value={refundDialog.reason}
                        onChange={(e) => setRefundDialog({ ...refundDialog, reason: e.target.value })}
                        placeholder="e.g. service outage credit"
                        multiline
                        minRows={2}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRefundDialog} disabled={refundSubmitting}>Cancel</Button>
                    <Button
                        variant="contained"
                        sx={{ bgcolor: '#8B5CF6', '&:hover': { bgcolor: '#7C3AED' } }}
                        startIcon={refundSubmitting ? <CircularProgress size={16} color="inherit" /> : <RefundIcon />}
                        onClick={handleRefundPayment}
                        disabled={refundSubmitting || !!refundAmountError || !refundDialog.reason.trim()}
                    >
                        Issue Refund
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Send Payment Link dialog -- WhatsApp / copy-link / email delivery */}
            <Dialog open={sendLinkDialog.open} onClose={closeSendLinkDialog} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Send Payment Link</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        A fresh payment link was created for <strong>{sendLinkDialog.payment?.customer_name}</strong>.
                    </Typography>
                    <TextField
                        fullWidth
                        label="Payment link"
                        value={sendLinkDialog.payUrl}
                        InputProps={{ readOnly: true }}
                        sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                        {sendLinkWhatsAppUrl() && (
                            <Button variant="outlined" startIcon={<WhatsAppIcon />} component="a"
                                href={sendLinkWhatsAppUrl()} target="_blank" rel="noopener noreferrer"
                                sx={{ borderColor: alpha('#25D366', 0.4), color: '#25D366' }}>
                                WhatsApp
                            </Button>
                        )}
                        <Button variant="outlined" startIcon={<ContentCopyIcon />} onClick={copyPaymentLink}>
                            Copy Link
                        </Button>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Or send by email</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            type="email"
                            placeholder="customer@example.com"
                            value={emailAddress}
                            onChange={(e) => setEmailAddress(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon fontSize="small" /></InputAdornment> }}
                        />
                        <Button variant="contained" onClick={emailPaymentLink} disabled={emailSending || !emailAddress.trim()}>
                            {emailSending ? <CircularProgress size={18} color="inherit" /> : 'Send'}
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeSendLinkDialog}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Receipt Modal */}
            {/* --- UPDATED: Single Receipt Modal with better logic --- */}
            <Modal open={showReceiptModal} onClose={() => setShowReceiptModal(false)}>
                <Box sx={modalStyle}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>Print Receipt</Typography>
                        <IconButton onClick={() => setShowReceiptModal(false)} size="small"><CloseIcon /></IconButton>
                    </Box>
                    {receiptData && (
                        <Box sx={{ textAlign: 'center', p: 4 }}>
                            <Typography sx={{ mb: 3 }}>Ready to print receipt for {receiptData.customer_name}.</Typography>
                            <Button variant="contained" color="primary" onClick={handlePrint} startIcon={<PrintIcon />}>
                                Print
                            </Button>
                        </Box>
                    )}
                </Box>
            </Modal>

            {/* --- NEW: Dialog for generating future payments --- */}
            <Dialog open={showGenerateModal} onClose={() => setShowGenerateModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Generate Future Payments</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                select
                                label="For Customer"
                                value={generateOptions.customer_id}
                                onChange={(e) => setGenerateOptions({ ...generateOptions, customer_id: e.target.value })}
                            >
                                <MenuItem value="all">All Active Customers</MenuItem>
                                {customers.map(customer => (
                                    <MenuItem key={customer.id} value={customer.id}>{customer.name}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                select
                                label="Generate Until"
                                value={generateOptions.until}
                                onChange={(e) => setGenerateOptions({ ...generateOptions, until: e.target.value })}
                            >
                                <MenuItem value="end_of_current_month">End of This Month</MenuItem>
                                <MenuItem value="end_of_next_month">End of Next Month</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowGenerateModal(false)}>Cancel</Button>
                    <Button onClick={handleGenerateFuturePayments} variant="contained">Generate</Button>
                </DialogActions>
            </Dialog>


            {/* --- NEW: Combined Receipt Modal for Unpaid Statement --- */}
            <Modal open={showCombinedReceiptModal} onClose={() => setShowCombinedReceiptModal(false)}>
                <Box sx={modalStyle} id="combined-receipt-content">
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>Unpaid Statement</Typography>
                        <IconButton onClick={() => setShowCombinedReceiptModal(false)} size="small"><CloseIcon /></IconButton>
                    </Box>
                    {combinedReceiptData && (
                        <Box>
                            <Box sx={{ mb: 2, textAlign: 'center' }}>
                                {combinedReceiptData.business_logo_url && <img src={`${apiService.baseURL}${combinedReceiptData.business_logo_url}`} alt="Business Logo" style={{ height: '48px', marginBottom: '8px' }} />}
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{combinedReceiptData.business_name}</Typography>
                                <Typography variant="body2">{combinedReceiptData.business_address}</Typography>
                                <Typography variant="body2">Mobile: {combinedReceiptData.business_mobile}</Typography>
                            </Box>
                            <Divider sx={{ my: 2 }} />
                            <Box sx={{ mb: 2 }}>
                                <Typography><strong>To:</strong> {combinedReceiptData.customer_name}</Typography>
                                <Typography><strong>Phone:</strong> {combinedReceiptData.customer_phone}</Typography>
                                <Typography><strong>Date:</strong> {combinedReceiptData.statement_date}</Typography>
                            </Box>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="h6" sx={{ mb: 1 }}>Pending Payments:</Typography>
                            <Paper variant="outlined" sx={{ mb: 2, background: '#f9f9f9' }}>
                                {combinedReceiptData.unpaid_items.map((item, index) => (
                                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', p: 1.5, borderBottom: index !== combinedReceiptData.unpaid_items.length - 1 ? '1px solid #eee' : 'none' }}>
                                        <Box>
                                            <Typography>{item.description}</Typography>
                                            <Typography variant="caption" color="text.secondary">Date: {item.date}</Typography>
                                        </Box>
                                        <Typography sx={{ fontWeight: '500' }}>${item.amount.toFixed(2)}</Typography>
                                    </Box>
                                ))}
                            </Paper>
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="h6"><strong>Total Due:</strong> <span style={{ color: theme.palette.error.main, fontWeight: 'bold' }}>${combinedReceiptData.total_unpaid_balance.toFixed(2)}</span></Typography>
                                <Typography><strong>Current Account Balance:</strong> <span style={{ fontWeight: 'bold' }}>${combinedReceiptData.customer_current_balance.toFixed(2)}</span></Typography>
                            </Box>
                            <Button variant="contained" color="primary" onClick={() => window.print()} sx={{ mt: 3, width: '100%' }}>Print Statement</Button>
                        </Box>
                    )}
                </Box>
            </Modal>
            
        </Box>
    );
};

export default PaymentsView;
